import { FieldModel, FieldQueryOptions, FieldQuerySortFields, SortModel } from '@cibo/core'
import { DataGridPro, EmptyState } from '@cibo/ui'
import { Box, TablePagination, Typography, useTheme } from '@mui/material'
import {
  GRID_CHECKBOX_SELECTION_COL_DEF,
  gridPageSelector,
  gridPageSizeSelector,
  GridPinnedColumnFields,
  GridRow,
  gridRowCountSelector,
  GridSlotProps,
  GridSortModel,
  PaginationPropsOverrides,
  useGridApiContext,
  useGridSelector,
} from '@mui/x-data-grid-pro'
import { uniq } from 'ramda'
import { ChangeEvent, MouseEvent, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { usePaginatedFields } from '../../queries'
import { PAGE_SIZES, PaginatedFieldsContext } from './PaginatedFieldsContexts'
import { PaginatedFieldsTableProps } from './PrivatePaginatedFieldsTableTypes'
import { DEFAULT_PAGINATED_FIELDS_COLUMNS } from './columnConfigurations'

const CiboPagination = (props: PaginationPropsOverrides) => {
  const apiRef = useGridApiContext()
  const gridPage = useGridSelector(apiRef, gridPageSelector)
  const pageSize = useGridSelector(apiRef, gridPageSizeSelector)
  const rowCount = useGridSelector(apiRef, gridRowCountSelector)
  const theme = useTheme()

  const select = useRef<HTMLSelectElement>(null)

  const handlePageChange = (event: MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    apiRef.current.setPage(newPage)
  }
  const handleChangeRowsPerPage = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | null
  ) => {
    event?.target.value && apiRef.current.setPageSize(Number(event.target.value))
  }

  useEffect(() => select.current?.children[0]?.setAttribute('role', 'listbox'), [select.current])

  return (
    <TablePagination
      {...props}
      component="div"
      ref={select}
      count={rowCount || -1}
      slotProps={{
        select: {
          sx: {
            borderWidth: 2,
            borderStyle: 'solid',
            borderColor: 'transparent',
            borderRadius: 'var(--mui-shape-borderRadius, 4px)',
            '&.Mui-focused': {
              borderColor: theme.palette.primary.main,
            },
          },
          renderValue: value => (
            <Typography fontSize="inherit" marginTop={0.5}>
              {value as number}
            </Typography>
          ),
        },
      }}
      page={gridPage}
      onPageChange={handlePageChange}
      rowsPerPage={pageSize}
      onRowsPerPageChange={handleChangeRowsPerPage}
    />
  )
}

export const PaginatedFieldsTable = ({
  baseQuery = {},
  columns,
  columnVisibilityModel,
  slots = {},
  dataLoading = false,
  initialSort,
  onRowsChange,
  pinnedColumns = {},
  mutateSortModel,
  hidePageSizeOptions,
  ...dataGridProProps
}: PaginatedFieldsTableProps<FieldModel>) => {
  const { t } = useTranslation('@cibo/landmanager/FieldsTable')

  const chosenColumns = useMemo(
    () => (!!columns ? columns : DEFAULT_PAGINATED_FIELDS_COLUMNS(t)),
    []
  )

  const { page, filters, setPage, pageSize, setPageSize } = useContext(PaginatedFieldsContext)

  const [sortModel, setSortModel] = useState<GridSortModel>(
    !!initialSort ? (initialSort as GridSortModel) : []
  )
  const [sortQuery, setSortQuery] = useState<FieldQueryOptions>({
    sort: sortModel as SortModel<FieldQuerySortFields>,
  })

  const requestQuery: FieldQueryOptions = {
    ...baseQuery,
    ...filters,
    ...sortQuery,
    sort: [...(baseQuery.sort || []), ...(sortQuery.sort || [])],
  }
  const paginatedFields = usePaginatedFields(page, pageSize, requestQuery, {
    refetchOnMount: 'always',
  })
  const allFields = usePaginatedFields(page, 0, undefined, { refetchOnMount: 'always' })
  const rows = paginatedFields.data ? paginatedFields.data.items : []

  const handleSortModelChange = (newSortModel: GridSortModel) => {
    const mutatedSortModel = mutateSortModel ? mutateSortModel(newSortModel) : newSortModel

    const { sort, ...newQuery } = sortQuery
    setSortModel(mutatedSortModel)
    setSortQuery({
      ...newQuery,
      sort: mutatedSortModel as SortModel<FieldQuerySortFields>,
    })
    setPage(0)
  }

  if (paginatedFields.data?.numItems === 0) {
    return (
      <>
        {/* NOTE: this is not ideal, however in some places we handle this as a component 
            and some places it is handled outside of the DataGrid */}
        {slots.toolbar && (
          <Box mx={2} my={1}>
            <slots.toolbar {...dataGridProProps.slotProps?.toolbar} />
          </Box>
        )}
        <EmptyState
          data-testid="emptyState"
          headerMarkdown={
            !!requestQuery.fullText
              ? t('noResultsHeaderFullTextMd', {
                  fullText: requestQuery.fullText,
                })
              : allFields.data?.numAvailable === 0
              ? t('addFieldsMd')
              : t('noResultsHeaderYourQueryMd')
          }
          helperText={
            requestQuery.fullText
              ? t('noResultsFullTextHelper')
              : allFields.data?.numAvailable === 0
              ? ''
              : t('noResultsQueryHelper')
          }
        />
      </>
    )
  }

  return (
    <Box sx={{ flex: 'flex', flexDirection: 'column' }}>
      <DataGridPro<FieldModel>
        rows={rows}
        columns={chosenColumns}
        disableColumnFilter={true}
        initialState={{
          columns: {
            columnVisibilityModel,
          },
          pinnedColumns: {
            left: [GRID_CHECKBOX_SELECTION_COL_DEF.field] as string[],
            ...(pinnedColumns as GridPinnedColumnFields),
          },
          sorting: {
            sortModel,
          },
        }}
        loading={dataLoading || paginatedFields.isFetching}
        pagination
        paginationMode="server"
        pageSizeOptions={hidePageSizeOptions ? [pageSize] : uniq([...PAGE_SIZES, pageSize])}
        paginationModel={{ page, pageSize }}
        onPaginationModelChange={({ page, pageSize }) => {
          setPage(page)
          setPageSize(pageSize)
        }}
        sortingMode="server"
        onSortModelChange={handleSortModelChange}
        rowCount={paginatedFields.data?.numAvailable || -1}
        keepNonExistentRowsSelected
        slots={{
          pagination: slots.pagination || CiboPagination,
          row: (params: GridSlotProps['row']) => <GridRow data-testid={params.rowId} {...params} />,
          ...slots,
        }}
        {...dataGridProProps}
      />
    </Box>
  )
}
